"use client";

import { useCallback } from "react";
import { signIn } from "next-auth/react";
import { useLocale } from "next-intl";
import { HOME_PAGE_REDIRECT } from "@/_constants/keys";
import {
  clearSsoRegisterContext,
  setSsoRegisterContext,
} from "@/_utils/ssoRegisterContext";

type SsoProvider = "google" | "linkedin";

interface CandidateSsoOptions {
  fingerprintId: string;
  returnTo?: string;
}

interface RecruiterSsoOptions {
  returnTo?: string;
}

export function useSsoSignIn() {
  const locale = useLocale();

  const buildCallbackUrl = useCallback(
    (returnTo?: string) => {
      const defaultReturnTo = `/${locale}${HOME_PAGE_REDIRECT}`;
      const target = returnTo ?? defaultReturnTo;
      return `/${locale}/auth/authorized?returnTo=${encodeURIComponent(target)}`;
    },
    [locale],
  );

  const signInAsCandidate = useCallback(
    (
      provider: SsoProvider,
      { fingerprintId, returnTo }: CandidateSsoOptions,
    ) => {
      setSsoRegisterContext({
        role: "CANDIDATE",
        fingerprintId,
      });

      void signIn(provider, {
        redirect: true,
        callbackUrl: buildCallbackUrl(returnTo),
      });
    },
    [buildCallbackUrl],
  );

  const signInAsRecruiter = useCallback(
    (provider: SsoProvider, options?: RecruiterSsoOptions) => {
      clearSsoRegisterContext();

      void signIn(provider, {
        redirect: true,
        callbackUrl: buildCallbackUrl(options?.returnTo),
      });
    },
    [buildCallbackUrl],
  );

  return { signInAsCandidate, signInAsRecruiter };
}
